import { z } from "zod";
import { INTERVIEW_TYPES, SENIORITY, ROLES, DECISION_STATUS, RISK_LEVEL } from "./types.js";

export const signupSchema = z.object({
  companyName: z.string().min(2).max(120),
  name: z.string().min(2).max(120),
  email: z.string().email(),
  password: z.string().min(10).max(200),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const rubricDimensionSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  weight: z.number().min(0).max(1),
  description: z.string().default(""),
});

export const questionPlanItemSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  followups: z.array(z.string()).optional(),
  skill: z.string().optional(),
});

export const createInterviewSchema = z.object({
  title: z.string().min(2).max(160),
  type: z.enum(INTERVIEW_TYPES),
  roleTitle: z.string().min(2).max(160),
  seniority: z.enum(SENIORITY).default("mid"),
  language: z.string().default("English"),
  durationMinutes: z.number().int().min(5).max(90).default(30),
  intro: z.string().max(2000).optional(),
  // Hiring context from the interview builder (Step 1). All optional so the
  // minimal create path (title + type + role + rubric) keeps working.
  department: z.string().max(160).optional(),
  jobDescription: z.string().max(8000).optional(),
  requiredSkills: z.array(z.string().min(1).max(60)).max(40).default([]),
  workLocation: z.enum(["remote", "hybrid", "onsite"]).optional(),
  experience: z.string().max(120).optional(),
  objective: z.string().max(1000).optional(),
  rubric: z.array(rubricDimensionSchema).min(1),
  questionPlan: z.array(questionPlanItemSchema).default([]),
  tavusReplicaId: z.string().optional(),
  tavusPersonaId: z.string().optional(),
});

export const updateInterviewSchema = createInterviewSchema.partial();

export const inviteCandidateSchema = z.object({
  interviewId: z.string().uuid(),
  name: z.string().min(1).max(160),
  email: z.string().email(),
  // optional window the business wants to constrain scheduling to
  windowStart: z.string().datetime().optional(),
  windowEnd: z.string().datetime().optional(),
  // optional promo code; a valid one discounts this interview's price (100% => free)
  couponCode: z.string().trim().min(1).max(64).optional(),
});

// Platform-admin coupon generation. Defaults match "a unique code that expires in a week".
export const createCouponSchema = z.object({
  percentOff: z.number().int().min(1).max(100).default(100),
  expiresInDays: z.number().int().min(1).max(90).default(7),
  maxRedemptions: z.number().int().min(1).max(100_000).optional(),
  note: z.string().max(200).optional(),
});

export const inviteTeammateSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120),
  role: z.enum(ROLES).default("member"),
  // optional personal note included in the invite email
  message: z.string().max(1000).optional(),
});

export const updateTeammateSchema = z.object({
  role: z.enum(ROLES),
});

export const passwordResetRequestSchema = z.object({
  email: z.string().email(),
});

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(10),
  password: z.string().min(10).max(200),
});

const hexColor = z
  .string()
  .regex(/^#([0-9a-fA-F]{6})$/, "Use a hex color like #4f46e5")
  .or(z.literal(""))
  .optional();

export const companyProfileSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  logoUrl: z.string().url().max(500).or(z.literal("")).optional(),
  brandColor: hexColor,
  // Accent for candidate-facing CTAs/highlights (falls back to brandColor in the UI).
  accentColor: hexColor,
  tagline: z.string().max(140).or(z.literal("")).optional(),
  supportEmail: z.string().email().or(z.literal("")).optional(),
});

export const reviewDecisionSchema = z.object({
  decision: z.enum(["cleared", "rejected", "reinterview", "escalated"]),
  note: z.string().max(1000).optional(),
});

export const consentSchema = z.object({
  consent: z.literal(true),
  timezone: z.string().max(64).optional(),
});

export const candidateTimezoneSchema = z.object({
  timezone: z.string().min(1).max(64),
});

export const rescheduleRequestSchema = z.object({
  // ISO 8601 datetime the candidate wants to move to (already tz-resolved by the client).
  requestedAt: z.string().datetime(),
  timezone: z.string().max(64).optional(),
});

export const mfaCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
});

export const emailVerifyConfirmSchema = z.object({
  token: z.string().min(10),
});

export const clientIntegritySchema = z.object({
  blurCount: z.number().int().min(0).default(0),
  pasteCount: z.number().int().min(0).default(0),
  fullscreenExits: z.number().int().min(0).default(0),
  notes: z.string().max(1000).optional(),
});

export const pricingRatesSchema = z.object({
  tavusPerMinuteUsd: z.number().min(0),
  evalInputUsdPerMTok: z.number().min(0),
  evalOutputUsdPerMTok: z.number().min(0),
  schedulerCostUsd: z.number().min(0),
  emailsPerInterview: z.number().min(0),
  emailUsdPer1000: z.number().min(0),
  routerbenchMarkup: z.number().min(1),
  profitMultiplier: z.number().min(1),
  roundUpToCents: z.number().int().min(1),
});

export const platformSettingsSchema = z.object({
  tavusApiKey: z.string().optional(),
  tavusDefaultReplicaId: z.string().optional(),
  routerbenchApiKey: z.string().optional(),
  evalModel: z.string().optional(),
  schedulerModel: z.string().optional(),
  resendApiKey: z.string().optional(),
  resendFrom: z.string().optional(),
  stripeSecretKey: z.string().optional(),
  pricePerInterviewCents: z.number().int().min(0).optional(),
  pricePerMinuteCents: z.number().int().min(0).optional(),
  pricing: pricingRatesSchema.partial().optional(),
});

// Employer-side report actions on a candidate invitation.
export const addNoteSchema = z.object({
  body: z.string().min(1).max(4000),
});

export const updateDecisionSchema = z.object({
  status: z.enum(DECISION_STATUS),
  note: z.string().max(4000).optional(),
});

export const addEvidenceSchema = z.object({
  type: z.string().min(1).max(120),
  riskLevel: z.enum(RISK_LEVEL).default("low"),
  note: z.string().max(2000).default(""),
  imageUrl: z.string().url().max(2000).optional(),
  at: z.string().datetime().optional(),
});

export type AddNoteInput = z.infer<typeof addNoteSchema>;
export type UpdateDecisionInput = z.infer<typeof updateDecisionSchema>;
export type AddEvidenceInput = z.infer<typeof addEvidenceSchema>;

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateInterviewInput = z.infer<typeof createInterviewSchema>;
export type InviteCandidateInput = z.infer<typeof inviteCandidateSchema>;
export type CreateCouponInput = z.infer<typeof createCouponSchema>;
export type ClientIntegrityInput = z.infer<typeof clientIntegritySchema>;
export type PlatformSettingsInput = z.infer<typeof platformSettingsSchema>;
export type InviteTeammateInput = z.infer<typeof inviteTeammateSchema>;
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetConfirmInput = z.infer<typeof passwordResetConfirmSchema>;
export type CompanyProfileInput = z.infer<typeof companyProfileSchema>;
export type ReviewDecisionInput = z.infer<typeof reviewDecisionSchema>;
export type ConsentInput = z.infer<typeof consentSchema>;
export type RescheduleRequestInput = z.infer<typeof rescheduleRequestSchema>;
export type MfaCodeInput = z.infer<typeof mfaCodeSchema>;
export type EmailVerifyConfirmInput = z.infer<typeof emailVerifyConfirmSchema>;
